import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Link as MuiLink,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import type { AppError } from '../../api/contract';
import { useAuth } from '../../auth/AuthProvider';
import type { DataSource } from '../../config/capabilities';
import { useRepository } from '../../data/RepositoryProvider';
import { useResource } from '../../hooks/useResource';
import { paths } from '../../routes/paths';
import { DataSourceBadge } from '../../shared/data/DataSourceBadge';
import { PageState } from '../../shared/data/PageState';
import { MetadataList } from '../../shared/detail/MetadataList';
import { formatTimestamp, type TimeLanguage } from '../../shared/format/time';
import { PageHeader } from '../../shared/layout/PageHeader';
import { defaultPartnerListQuery, partnerRepositoryToken } from '../partners/repository';
import type { PartnerSummary } from '../partners/types';
import { PartnerCreditHealthPanel } from './PartnerCreditHealthPanel';
import { PartnerCreditWizard } from './PartnerCreditWizard';
import { partnerCreditRepositoryToken } from './repository';
import type {
  PartnerCreditApproval,
  PartnerCreditCertification,
  PartnerCreditConfiguration,
  PartnerCreditStatusInput,
  PartnerCreditVersion,
} from './types';

export function PartnerCreditPage({
  dataSource,
  healthDataSource,
}: {
  readonly dataSource: DataSource;
  readonly healthDataSource: DataSource;
}) {
  const { partnerId } = useParams();
  return partnerId === undefined ? (
    <PartnerCreditLandingPage dataSource={dataSource} />
  ) : (
    <PartnerCreditDetailPage
      dataSource={dataSource}
      healthDataSource={healthDataSource}
      partnerId={partnerId}
    />
  );
}

function PartnerCreditLandingPage({ dataSource }: { readonly dataSource: DataSource }) {
  const partners = useRepository(partnerRepositoryToken);
  const partnerCredit = useRepository(partnerCreditRepositoryToken);
  const { t } = useTranslation();
  const [query, setQuery] = useState(defaultPartnerListQuery);
  const [refreshKey, setRefreshKey] = useState(0);
  const resource = useResource(
    async (signal) => {
      const page = await partners.list(query, signal);
      const rows = await Promise.all(
        page.items.map(async (partner) => ({
          partner,
          configuration: await partnerCredit.get(partner.id, signal),
        })),
      );
      return { page, rows };
    },
    [partnerCredit, partners, query, refreshKey],
    ({ page }) => page.items.length === 0,
  );
  const total = resource.data?.page.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / query.pageSize));
  return (
    <Stack spacing={2} sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('partnerCredit.subtitle')}
        title={t('partnerCredit.title')}
      />
      <PageState
        emptyMessage={t('partnerCredit.emptyPartners')}
        error={safePageError(resource.error, t('partnerCredit.errors.load'))}
        onRetry={() => setRefreshKey((value) => value + 1)}
        status={resource.status}
      >
        <Stack spacing={1}>
          <Stack
            aria-label={t('partnerCredit.partnerList')}
            component="ul"
            spacing={1}
            sx={{ p: 0 }}
          >
            {resource.data?.rows.map(({ configuration, partner }) => (
              <PartnerSummaryCard
                configuration={configuration}
                key={partner.id}
                partner={partner}
              />
            ))}
          </Stack>
          {resource.data === null ? null : (
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
            >
              <Typography color="text.secondary" variant="caption">
                {t('partnerCredit.pagination.summary', {
                  page: query.page,
                  pages,
                  total,
                })}
              </Typography>
              <Stack direction="row" spacing={1}>
                <Button
                  disabled={query.page <= 1}
                  onClick={() => setQuery((current) => ({ ...current, page: current.page - 1 }))}
                  size="small"
                >
                  {t('partnerCredit.pagination.previous')}
                </Button>
                <Button
                  disabled={query.page >= pages}
                  onClick={() => setQuery((current) => ({ ...current, page: current.page + 1 }))}
                  size="small"
                >
                  {t('partnerCredit.pagination.next')}
                </Button>
              </Stack>
            </Stack>
          )}
        </Stack>
      </PageState>
    </Stack>
  );
}

function PartnerSummaryCard({
  configuration,
  partner,
}: {
  readonly configuration: PartnerCreditConfiguration;
  readonly partner: PartnerSummary;
}) {
  const { t } = useTranslation();
  const configured = configuration.id !== null && configuration.currentVersion !== null;
  return (
    <Paper component="li" sx={{ listStyle: 'none', p: 2 }} variant="outlined">
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ alignItems: { sm: 'center' }, justifyContent: 'space-between' }}
      >
        <div>
          <Typography component="h2" variant="h6">
            {partner.name}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {partner.code} · {partner.id}
          </Typography>
        </div>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <Chip
            color={configured ? 'success' : 'default'}
            label={configured ? t('partnerCredit.configured') : t('partnerCredit.notConfigured')}
            size="small"
          />
          <Button
            aria-label={t('partnerCredit.actions.openPartner', { partner: partner.name })}
            component={Link}
            to={`${paths.partnerCredit}/${encodeURIComponent(partner.id)}`}
          >
            {t('actions.view')}
          </Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

function PartnerCreditDetailPage({
  dataSource,
  healthDataSource,
  partnerId,
}: {
  readonly dataSource: DataSource;
  readonly healthDataSource: DataSource;
  readonly partnerId: string;
}) {
  const repository = useRepository(partnerCreditRepositoryToken);
  const auth = useAuth();
  const { i18n, t } = useTranslation();
  const language: TimeLanguage = i18n.language.startsWith('th') ? 'th' : 'en';
  const [refreshKey, setRefreshKey] = useState(0);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [certifyOpen, setCertifyOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const [approval, setApproval] = useState<PartnerCreditApproval | null>(null);
  const resource = useResource(
    async (signal) => {
      const [configuration, versions] = await Promise.all([
        repository.get(partnerId, signal),
        repository.listVersions(partnerId, signal),
      ]);
      return { configuration, versions };
    },
    [partnerId, refreshKey, repository],
    () => false,
  );
  const canManage =
    auth.status === 'authenticated' && auth.principal.permissions.includes('INTEGRATION_MANAGE');
  const refresh = () => setRefreshKey((value) => value + 1);
  return (
    <Box sx={{ minWidth: 0 }}>
      <PageHeader
        actions={<DataSourceBadge source={dataSource} />}
        subtitle={t('partnerCredit.detailSubtitle', { partnerId })}
        title={t('partnerCredit.title')}
      />
      <PageState
        error={safePageError(resource.error, t('partnerCredit.errors.loadDetail'))}
        onRetry={refresh}
        status={resource.status}
      >
        {resource.data === null ? null : (
          <Stack spacing={2}>
            <IntegrationSummary configuration={resource.data.configuration} language={language} />
            {approval === null ? null : (
              <Alert severity="info">
                {t('partnerCredit.wizard.pendingApproval', { id: approval.id })}
              </Alert>
            )}
            {canManage ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <Button onClick={() => setWizardOpen(true)} variant="contained">
                  {t('partnerCredit.actions.createVersion')}
                </Button>
                <Button onClick={() => setCertifyOpen(true)}>
                  {t('partnerCredit.actions.certify')}
                </Button>
                {resource.data.configuration.status === null ? null : (
                  <Button color="warning" onClick={() => setStatusOpen(true)}>
                    {resource.data.configuration.status === 'ACTIVE'
                      ? t('partnerCredit.actions.suspend')
                      : t('partnerCredit.actions.activate')}
                  </Button>
                )}
              </Stack>
            ) : null}
            <VersionsTable partnerId={partnerId} versions={resource.data.versions} />
            <PartnerCreditHealthPanel dataSource={healthDataSource} partnerId={partnerId} />
          </Stack>
        )}
      </PageState>
      <PartnerCreditWizard
        currentConfiguration={
          resource.data?.configuration ?? {
            id: null,
            partnerId,
            name: null,
            status: null,
            currentVersion: null,
            createdAt: null,
            createdBy: null,
            updatedAt: null,
            updatedBy: null,
          }
        }
        dataSource={dataSource}
        onClose={() => setWizardOpen(false)}
        onPublished={(value) => setApproval(value)}
        open={wizardOpen}
        partnerId={partnerId}
      />
      <CertificationDialog
        onClose={() => setCertifyOpen(false)}
        open={certifyOpen}
        partnerId={partnerId}
      />
      {resource.data === null || resource.data.configuration.status === null ? null : (
        <StatusDialog
          configuration={resource.data.configuration}
          onClose={() => setStatusOpen(false)}
          onSaved={() => {
            setStatusOpen(false);
            refresh();
          }}
          open={statusOpen}
          partnerId={partnerId}
        />
      )}
    </Box>
  );
}

function IntegrationSummary({
  configuration,
  language,
}: {
  readonly configuration: PartnerCreditConfiguration;
  readonly language: TimeLanguage;
}) {
  const { t } = useTranslation();
  const current = configuration.currentVersion;
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" variant="h6">
        {t('partnerCredit.sections.current')}
      </Typography>
      {current === null ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          {t('partnerCredit.notConfigured')}
        </Alert>
      ) : (
        <Box sx={{ mt: 2 }}>
          <MetadataList
            items={[
              { label: t('partnerCredit.fields.name'), value: current.configuration.name },
              { label: t('partnerCredit.fields.status'), value: configuration.status ?? '—' },
              { label: t('partnerCredit.fields.version'), value: current.version },
              { label: t('partnerCredit.fields.authType'), value: current.configuration.authType },
              {
                label: t('partnerCredit.fields.keyReference'),
                value: current.configuration.hmacKeyId ?? t('common.notAvailable'),
              },
              {
                label: t('partnerCredit.fields.certifiedAt'),
                value: timestamp(current.certifiedAt, language, t('common.notAvailable')),
              },
              {
                label: t('partnerCredit.fields.publishedAt'),
                value: timestamp(current.publishedAt, language, t('common.notAvailable')),
              },
              {
                label: t('partnerCredit.fields.timeout'),
                value: `${current.configuration.timeoutMilliseconds} ms`,
              },
            ]}
          />
        </Box>
      )}
    </Paper>
  );
}

function VersionsTable({
  partnerId,
  versions,
}: {
  readonly partnerId: string;
  readonly versions: readonly PartnerCreditVersion[];
}) {
  const { t } = useTranslation();
  return (
    <Paper component="section" sx={{ p: 2 }} variant="outlined">
      <Typography component="h2" variant="h6">
        {t('partnerCredit.sections.versions')}
      </Typography>
      {versions.length === 0 ? (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {t('partnerCredit.emptyVersions')}
        </Typography>
      ) : (
        <TableContainer sx={{ mt: 1 }}>
          <Table aria-label={t('partnerCredit.versionTable')} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('partnerCredit.fields.version')}</TableCell>
                <TableCell>{t('partnerCredit.fields.name')}</TableCell>
                <TableCell>{t('partnerCredit.fields.authType')}</TableCell>
                <TableCell>{t('partnerCredit.fields.certification')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {versions.map((version) => (
                <TableRow key={version.id}>
                  <TableCell>
                    <MuiLink
                      component={Link}
                      to={`${paths.partnerCredit}/${encodeURIComponent(partnerId)}/versions/${encodeURIComponent(version.id)}`}
                    >
                      v{version.version}
                    </MuiLink>
                  </TableCell>
                  <TableCell>{version.configuration.name}</TableCell>
                  <TableCell>{version.configuration.authType}</TableCell>
                  <TableCell>{version.certifiedAt === null ? 'PENDING' : 'CERTIFIED'}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}

function CertificationDialog({
  onClose,
  open,
  partnerId,
}: {
  readonly onClose: () => void;
  readonly open: boolean;
  readonly partnerId: string;
}) {
  const repository = useRepository(partnerCreditRepositoryToken);
  const { t } = useTranslation();
  const [approvalRequestId, setApprovalRequestId] = useState('');
  const [result, setResult] = useState<PartnerCreditCertification | null>(null);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  const valid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/iu.test(
    approvalRequestId,
  );
  const submit = async () => {
    if (!valid || submittingRef.current) return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const response = await repository.certify(partnerId, { approvalRequestId });
      setResult(response.data);
    } catch (cause) {
      setError(safePageError(cause, t('partnerCredit.errors.certify')));
      submittingRef.current = false;
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (busy) return;
    setApprovalRequestId('');
    setResult(null);
    setError(null);
    submittingRef.current = false;
    onClose();
  };
  return (
    <Dialog
      aria-describedby="partner-credit-certification-description"
      fullWidth
      onClose={close}
      open={open}
    >
      <DialogTitle>{t('partnerCredit.certification.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText id="partner-credit-certification-description">
            {t('partnerCredit.certification.description')}
          </DialogContentText>
          <Alert severity="info">{t('partnerCredit.certification.nonFinancial')}</Alert>
          {error === null ? null : <SafeError error={error} />}
          {result === null ? (
            <TextField
              disabled={busy}
              error={approvalRequestId.length > 0 && !valid}
              label={t('partnerCredit.fields.approvalRequestId')}
              onChange={(event) => setApprovalRequestId(event.target.value.trim())}
              required
              value={approvalRequestId}
            />
          ) : (
            <Alert severity="success">
              {t('partnerCredit.certification.passed', {
                health: result.healthHttpStatus,
                status: result.statusHttpStatus,
              })}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {result === null ? t('actions.cancel') : t('actions.close')}
        </Button>
        {result === null ? (
          <Button
            disabled={!valid || busy}
            loading={busy}
            onClick={() => void submit()}
            variant="contained"
          >
            {t('partnerCredit.actions.runCertification')}
          </Button>
        ) : null}
      </DialogActions>
    </Dialog>
  );
}

export function StatusDialog({
  configuration,
  onClose,
  onSaved,
  open,
  partnerId,
}: {
  readonly configuration: PartnerCreditConfiguration;
  readonly onClose: () => void;
  readonly onSaved: () => void;
  readonly open: boolean;
  readonly partnerId: string;
}) {
  const repository = useRepository(partnerCreditRepositoryToken);
  const { t } = useTranslation();
  const [reason, setReason] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [error, setError] = useState<Pick<AppError, 'message' | 'requestId'> | null>(null);
  const [busy, setBusy] = useState(false);
  const submittingRef = useRef(false);
  useEffect(() => {
    setReason('');
    setConfirmed(false);
    setError(null);
    setBusy(false);
    submittingRef.current = false;
  }, [open]);
  const proposed: PartnerCreditStatusInput['status'] =
    configuration.status === 'ACTIVE' ? 'SUSPENDED' : 'ACTIVE';
  const label =
    proposed === 'SUSPENDED'
      ? t('partnerCredit.actions.suspend')
      : t('partnerCredit.actions.activate');
  const submit = async () => {
    const normalizedReason = reason.trim();
    if (
      normalizedReason.length === 0 ||
      normalizedReason.length > 2000 ||
      !confirmed ||
      submittingRef.current
    )
      return;
    submittingRef.current = true;
    setBusy(true);
    setError(null);
    try {
      await repository.changeStatus(partnerId, { status: proposed, reason: normalizedReason });
      setReason('');
      setConfirmed(false);
      setError(null);
      onSaved();
    } catch (cause) {
      setError(safePageError(cause, t('partnerCredit.errors.status')));
      submittingRef.current = false;
    } finally {
      setBusy(false);
    }
  };
  const close = () => {
    if (busy) return;
    setReason('');
    setConfirmed(false);
    setError(null);
    submittingRef.current = false;
    onClose();
  };
  return (
    <Dialog aria-describedby="partner-credit-status-impact" fullWidth onClose={close} open={open}>
      <DialogTitle>{label}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <DialogContentText id="partner-credit-status-impact">
            {t('partnerCredit.status.impact', {
              current: configuration.status,
              proposed,
            })}
          </DialogContentText>
          {error === null ? null : <SafeError error={error} />}
          <TextField
            disabled={busy}
            label={t('partnerCredit.fields.reason')}
            minRows={2}
            multiline
            onChange={(event) => setReason(event.target.value)}
            required
            slotProps={{ htmlInput: { maxLength: 2000 } }}
            value={reason}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={confirmed}
                disabled={busy}
                onChange={(event) => setConfirmed(event.target.checked)}
                required
              />
            }
            label={t('partnerCredit.status.confirm')}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button disabled={busy} onClick={close}>
          {t('actions.cancel')}
        </Button>
        <Button
          color="warning"
          disabled={reason.trim().length === 0 || !confirmed || busy}
          loading={busy}
          onClick={() => void submit()}
          variant="contained"
        >
          {label}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function timestamp(value: string | null, language: TimeLanguage, empty: string) {
  if (value === null) return empty;
  const formatted = formatTimestamp(value, 'Asia/Bangkok', language);
  return `${formatted.display} (${formatted.originalOffset})`;
}

function safePageError(
  cause: unknown,
  fallback: string,
): Pick<AppError, 'message' | 'requestId'> | null {
  if (cause === null) return null;
  if (typeof cause !== 'object' || cause === null) return { message: fallback, requestId: null };
  const candidate = cause as { readonly requestId?: unknown };
  return {
    message: fallback,
    requestId: typeof candidate.requestId === 'string' ? candidate.requestId : null,
  };
}

function SafeError({ error }: { readonly error: Pick<AppError, 'message' | 'requestId'> }) {
  return (
    <Alert severity="error">
      {error.message}
      {error.requestId === null ? null : ` Request ID: ${error.requestId}`}
    </Alert>
  );
}
